import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  SkeletonAnimation,
  SkeletonPreset,
  SkeletonSize,
  SkeletonType,
} from './skeleton-loader.types';
import {
  SkeletonFormComponent,
  SkeletonFormFieldComponent,
  SkeletonFormFlatComponent,
  SkeletonFullLayoutComponent,
  SkeletonHeaderComponent,
  SkeletonNavbarComponent,
  SkeletonPageComponent,
  SkeletonProfileComponent,
  SkeletonSectionComponent,
  SkeletonStatTilesComponent,
  SkeletonTableComponent,
  SkeletonTeamWorkloadComponent,
} from './components';

export type {
  SkeletonAnimation,
  SkeletonPreset,
  SkeletonSize,
  SkeletonType,
} from './skeleton-loader.types';

@Component({
  selector: 'cm-skeleton-loader',
  standalone: true,
  imports: [
    SkeletonSectionComponent,
    SkeletonFormComponent,
    SkeletonFormFlatComponent,
    SkeletonFormFieldComponent,
    SkeletonHeaderComponent,
    SkeletonNavbarComponent,
    SkeletonPageComponent,
    SkeletonFullLayoutComponent,
    SkeletonTableComponent,
    SkeletonStatTilesComponent,
    SkeletonTeamWorkloadComponent,
    SkeletonProfileComponent,
  ],
  templateUrl: './skeleton-loader.component.html',
  styleUrl: './skeleton-loader.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkeletonLoaderComponent {
  public readonly type = input<SkeletonType>('rectangle');
  public readonly size = input<SkeletonSize>('md');
  public readonly animation = input<SkeletonAnimation>('shimmer');
  public readonly width = input<string | undefined>(undefined);
  public readonly height = input<string | undefined>(undefined);
  public readonly count = input<number>(1);
  public readonly borderRadius = input<string | undefined>(undefined);
  public readonly preset = input<SkeletonPreset>(null);

  /** Visually-hidden text announced to assistive tech while the skeleton shows (WCAG 4.1.3). */
  public readonly loadingText = input<string>('Loading…');

  // Backwards-compatible alias for older [rows] usage
  public readonly rows = input<number | undefined>(undefined);

  // Table preset configuration
  public readonly tableRows = input<number>(5);
  public readonly tableColumns = input<number>(4);

  // Stat tiles preset configuration
  public readonly tileCount = input<number>(4);

  // Team workload preset configuration
  public readonly cardCount = input<number>(4);
  public readonly rowsPerCard = input<number>(3);
  public readonly teamRows = input<number>(4);

  public readonly effectiveCount = computed(() => {
    const rows = this.rows();
    if (rows != null && rows >= 0) {
      return rows;
    }
    return this.count();
  });

  public readonly className = computed(() => this._getClassName());

  private _getClassName(): string {
    const classes = [
      'cm-skeleton',
      `cm-skeleton--${this.type()}`,
      `cm-skeleton--${this.size()}`,
      `cm-skeleton--${this.animation()}`,
    ];

    return classes.join(' ');
  }
}
